import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Loader2 } from 'lucide-react';
import { getFormationRdvs, getUserEvents } from '../../api/formation';
import { useAuth } from '../../context/AuthContext';

// Résumé d'agenda logé dans le tiers bas de la barre latérale.
//
// Volontairement en lecture seule : la création, la semaine complète et la
// synchro Google restent dans PlanningSidebar, ouvert par « Voir tout ».
// Dupliquer ces fonctions ici ferait deux endroits à maintenir pour le même
// travail.
//
// Fenêtre de 14 jours : au-delà, ce n'est plus « ce qui arrive », c'est du
// planning, et ça se consulte dans le panneau complet.
const FENETRE_JOURS = 14;
const MAX_AFFICHES = 4;

function isoLocal(d) {
  return d.toLocaleDateString('fr-CA', { timeZone: 'Pacific/Tahiti' });
}

// « Aujourd'hui 14:00 », « Demain 10:30 », sinon « lun. 18 · 09:00 ».
function libelleQuand(dateISO, heure) {
  const jour = String(dateISO || '').slice(0, 10);
  const hhmm = String(heure || '').slice(0, 5);
  const aujourdhui = isoLocal(new Date());
  const demainD = new Date();
  demainD.setDate(demainD.getDate() + 1);
  const demain = isoLocal(demainD);

  if (jour === aujourdhui) return `Aujourd'hui${hhmm ? ` ${hhmm}` : ''}`;
  if (jour === demain) return `Demain${hhmm ? ` ${hhmm}` : ''}`;
  const d = new Date(jour + 'T12:00:00');
  const court = d.toLocaleDateString('fr-FR', { timeZone: 'Pacific/Tahiti', weekday: 'short', day: 'numeric' });
  return `${court}${hhmm ? ` · ${hhmm}` : ''}`;
}

export default function AgendaWidget({ onOpenPlanning }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Sans id utilisateur, axios retirerait user_id des paramètres et le
    // serveur renverrait les événements de tout le monde. On attend.
    if (!user?.id) return;
    let vivant = true;
    const debut = new Date();
    const fin = new Date();
    fin.setDate(fin.getDate() + FENETRE_JOURS);
    const params = { from_date: isoLocal(debut), to_date: isoLocal(fin) };

    Promise.all([
      // formateur_id, comme user_id plus bas : les deux routes renvoient les
      // lignes de TOUS les utilisateurs quand le filtre est absent. Ce widget
      // est l'agenda personnel — le planning d'équipe reste dans le panneau
      // complet, ouvert par « Voir tout l'agenda ».
      getFormationRdvs({ ...params, formateur_id: user?.id }).catch(() => ({ data: [] })),
      // user_id est INDISPENSABLE : sans lui, GET /formation/events renvoie les
      // événements de tous les utilisateurs, et on affiche les rendez-vous
      // personnels des collègues dans « mon agenda ».
      getUserEvents({ ...params, user_id: user?.id }).catch(() => ({ data: [] })),
    ]).then(([rdvs, events]) => {
      if (!vivant) return;
      // RDV de formation : l'apprenant est le vrai sujet de la ligne. Le champ
      // `sujet` (souvent « Session 5 ») ne dit rien seul — il passe en détail.
      const listeRdvs = (Array.isArray(rdvs.data) ? rdvs.data : []).map(r => ({
        id: `rdv-${r.id}`,
        type: 'Formation',
        titre: r.apprenant_nom || r.sujet || 'Rendez-vous',
        detail: [r.sujet, r.formateur_nom].filter(Boolean).join(' · '),
        date: r.date_rdv,
        heure: r.heure_debut,
      }));
      const listeEvents = (Array.isArray(events.data) ? events.data : []).map(e => ({
        id: `ev-${e.id}`,
        type: 'Perso',
        titre: e.titre || 'Événement',
        detail: e.est_visio ? 'Visio' : (e.lieu || ''),
        date: e.date_event,
        heure: e.heure_debut,
      }));
      // Tri chronologique : la date est en AAAA-MM-JJ, donc comparable
      // lexicographiquement — pas de Date() à construire, pas de piège de
      // fuseau (l'app est en Pacific/Tahiti).
      const tout = [...listeRdvs, ...listeEvents]
        .filter(x => x.date)
        .sort((a, b) => {
          const da = String(a.date).slice(0, 10);
          const db = String(b.date).slice(0, 10);
          if (da !== db) return da.localeCompare(db);
          return String(a.heure || '').localeCompare(String(b.heure || ''));
        });
      setItems(tout.slice(0, MAX_AFFICHES));
      setLoading(false);
    });

    return () => { vivant = false; };
    // Dépend de l'utilisateur : au montage, l'auth peut ne pas être résolue et
    // user.id serait vide — la requête repartirait sans filtre.
  }, [user?.id]);

  return (
    <div className="flex flex-col gap-2 h-full min-h-0">
      <h3 className="text-sm font-semibold text-on-surface flex items-center gap-2 shrink-0">
        <Calendar size={16} className="text-primary" />
        Agenda
      </h3>

      <div className="flex-1 overflow-y-auto flex flex-col gap-2 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-4 text-outline">
            <Loader2 size={14} className="animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-xs text-outline py-2">Rien de prévu sur {FENETRE_JOURS} jours.</p>
        ) : (
          items.map(it => (
            <button
              key={it.id}
              onClick={onOpenPlanning}
              className="text-left bg-surface border border-outline-variant rounded-lg p-2.5 text-xs hover:border-primary/50 transition-colors"
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                {/* Pastille de provenance : sans elle, « Session 5 » ne dit pas
                    s'il s'agit d'une formation ou d'un rendez-vous perso. */}
                <span className={`text-[9px] px-1.5 py-0.5 rounded shrink-0 ${
                  it.type === 'Formation'
                    ? 'bg-tertiary/15 text-tertiary'
                    : 'bg-secondary/15 text-secondary'
                }`}>
                  {it.type}
                </span>
                <p className="font-medium text-on-surface truncate">{it.titre}</p>
              </div>
              {it.detail && <p className="text-on-surface-variant truncate mb-0.5">{it.detail}</p>}
              <p className="text-outline flex items-center gap-1">
                <Clock size={11} className="shrink-0" />
                {libelleQuand(it.date, it.heure)}
              </p>
            </button>
          ))
        )}
      </div>

      <button
        onClick={onOpenPlanning}
        className="text-xs text-primary hover:underline w-full text-center shrink-0 py-1"
      >
        Voir tout l'agenda
      </button>
    </div>
  );
}
